import {useFocusEffect, useNavigation} from '@react-navigation/native';
import React, {useCallback, useState} from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import Header from '../../components/Header/Header';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import {Colors} from '../../constants/Colors';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes} from '../../navigation/Routes.types';
import ScheduledProposalService, {
  ScheduledOrderMenu as Menu,
  ScheduledOrderMenuItem,
} from '../../services/ScheduledProposalService/ScheduledProposalService';
import {useUserStore} from '../../store/UserStore';
import {styles} from './styles';

const VAZIO: Menu = {
  emConsulta: [],
  pendentes: [],
  emCancelamento: [],
  historico: [],
};

/** `2026-08-14` -> `14/08`. Sem `new Date`: o parse de data pura vira UTC e
 * volta um dia no fuso do Brasil. */
const diaMes = (iso?: string | null): string => {
  if (!iso) {
    return '';
  }
  const [, mes, dia] = iso.split('-');
  return dia && mes ? `${dia}/${mes}` : iso;
};

const diaMesAno = (iso?: string | null): string => {
  if (!iso) {
    return '';
  }
  const [ano, mes, dia] = iso.split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : iso;
};

/**
 * PED-13 RN-23 — o menu DEDICADO de pedidos programados.
 *
 * ## Por que uma tela própria, e não só a notificação
 *
 * O refino dá as duas razões, e a segunda é a que obriga: *"o aceite não pode
 * depender exclusivamente de notificação, que se perde na caixa do cliente. E,
 * como o carrinho é destruído quando uma tentativa de pagamento falha (RN-16),
 * **o menu é o único caminho de volta** — sem ele, o cliente que abandona um
 * PIX não tem como retomar a proposta."*
 *
 * ## As três seções
 *
 * | Seção | O que é |
 * |---|---|
 * | Em consulta | fora da janela — marcadas com a data em que a confirmação abre (RN-18) |
 * | Aguardando confirmação | acionáveis agora, inclusive as de pagamento frustrado |
 * | Histórico | passadas, indicando se foram aceitas ou não |
 *
 * A primeira só passou a existir porque o refino revogou a retenção (RN-09 a):
 * antes, a proposta chegava ao CB já dentro da janela e "recebida" era sinônimo
 * de "acionável". RN-18 foi revisada depois para o a prazo nascer acionável
 * desde a criação pelo CS — então, na prática, "Em consulta" só ainda recebe
 * conjunto anterior a essa mudança; a seção continua existindo para não
 * quebrar contrato com o backend.
 *
 * ## Recarrega a cada foco, de propósito
 *
 * O carrinho é reconstruído a cada entrada (RN-16) e a janela de aceite abre
 * por passagem de data. Uma lista em cache mostraria "confirmação disponível a
 * partir de 12/08" no dia 12.
 */
/**
 * PAG-01 (26/08/2026) — rede contra BACKEND ANTIGO.
 *
 * O servidor novo já não devolve conjunto à vista aqui
 * (`ScheduledOrderMenuController` filtra por `->aPrazo()`), mas o app é
 * publicado e adotado numa janela que não coincide com o deploy do backend. No
 * intervalo, um app novo contra backend antigo mostraria o MESMO pedido nas
 * duas telas — aqui como "programado" (rótulo que virou mentira quando o ramo
 * de renderização saiu) e em "Meus pedidos" como a pagar.
 *
 * Filtrar na borda é o que mantém a tela honesta nos dois cenários, e é barato:
 * com o backend novo a lista já vem sem nenhum, e o filtro é um no-op.
 */
const semAVista = (menu: Menu): Menu => ({
  emConsulta: menu.emConsulta.filter(i => i.paymentMode !== 'A_VISTA'),
  pendentes: menu.pendentes.filter(i => i.paymentMode !== 'A_VISTA'),
  emCancelamento: menu.emCancelamento.filter(i => i.paymentMode !== 'A_VISTA'),
  historico: menu.historico.filter(i => i.paymentMode !== 'A_VISTA'),
});

const ScheduledOrderMenu = () => {
  const {navigate, goBack} = useNavigation<NavigationProps>();
  const {user} = useUserStore();
  const clientCode: string =
    (user?.current_client?.clientCode ?? user?.clientCode) || '';

  const [menu, setMenu] = useState<Menu>(VAZIO);
  const [loading, setLoading] = useState<boolean>(true);
  const [erro, setErro] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const carregar = useCallback(
    async (comSpinner: boolean) => {
      if (!clientCode) {
        setLoading(false);
        return;
      }
      if (comSpinner) {
        setLoading(true);
      }
      setErro(false);
      try {
        setMenu(semAVista(await ScheduledProposalService.GetScheduledOrderMenu(clientCode)));
      } catch (error) {
        console.error('Erro ao carregar os pedidos programados:', error);
        setErro(true);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [clientCode],
  );

  useFocusEffect(
    useCallback(() => {
      carregar(true);
    }, [carregar]),
  );

  const abrir = (item: ScheduledOrderMenuItem) => {
    // PAG-01 (26/08/2026) — o PEDIDO A PAGAR saiu desta tela. Ele não é
    // programado: vence hoje às 23:59, e esta tela se apresenta como
    // "Pedidos montados pelo seu vendedor para uma data futura". Agora ele é
    // listado em "Meus pedidos" (`Orders.tsx`), que é onde o cliente procura o
    // que tem a pagar agora — e o servidor já não o devolve aqui
    // (`ScheduledOrderMenuController` filtra por `->aPrazo()`).
    //
    // `paymentMode` FICA no tipo e no payload de propósito: app novo contra
    // backend antigo continuaria recebendo o conjunto à vista nesta lista, e
    // sem o campo não haveria como reconhecê-lo. Ver o ramo defensivo abaixo.
    navigate(AppRoutes.SCHEDULED_ORDER_REVIEW, {
      // A linha do menu é o CONJUNTO, e é o conjunto inteiro que o carrinho
      // abre: com n vendas, o cliente confirma tudo de uma vez (RN-20).
      setId: item.setId,
      // No agrupamento a proposta pode ser de outra loja do dono; sem carregar
      // o código junto, a tela de revisão consultaria com a loja errada e
      // levaria 404.
      clientCode: item.clientCode,
    });
  };

  const Card = ({
    item,
    secao,
  }: {
    item: ScheduledOrderMenuItem;
    secao: 'emConsulta' | 'pendentes' | 'emCancelamento' | 'historico';
  }) => (
    <TouchableOpacity
      style={styles.card}
      activeOpacity={secao === 'emCancelamento' ? 1 : 0.8}
      disabled={secao === 'emCancelamento'}
      onPress={() => abrir(item)}>
      <View style={styles.cardTopo}>
        {/* `referenceDate` é a data PROGRAMADA do pedido, não a da entrega —
            a entrega real vem do SAP e só é conhecida na revisão
            (`dataRealEntrega`, RN-24), onde pode até divergir desta. Chamar
            esta data de entrega prometia no menu um dia que a tela seguinte
            desmentia. Mesmo vocabulário de `ScheduledOrderReview`. */}
        <Text style={styles.cardTitulo}>
          {`Programado para ${diaMesAno(item.referenceDate)}`}
        </Text>
        {secao === 'historico' ? (
          <View
            style={[
              styles.selo,
              item.aceito ? styles.seloAceito : styles.seloNaoAceito,
            ]}>
            <Text
              style={[
                styles.seloTexto,
                item.aceito
                  ? styles.seloTextoAceito
                  : styles.seloTextoNaoAceito,
              ]}>
              {item.aceito ? 'Aceito' : 'Não aceito'}
            </Text>
          </View>
        ) : null}
      </View>

      <Text style={styles.cardLinha}>
        {item.vendas > 1
          ? `${item.vendas} pedidos · ${item.itens} ${
              item.itens === 1 ? 'item' : 'itens'
            }`
          : `${item.itens} ${item.itens === 1 ? 'item' : 'itens'}`}
        {item.temBonificacao
          ? item.bonificacoes > 1
            ? ` · ${item.bonificacoes} bonificações`
            : ' · com bonificação'
          : ''}
      </Text>

      {/* RN-18 — em consulta a data em que a confirmação abre é a informação
          principal do card: sem ela o cliente toca esperando aceitar. */}
      {secao === 'emConsulta' ? (
        <Text style={styles.cardAviso}>
          Confirmação disponível a partir de {diaMes(item.actionableFrom)}
        </Text>
      ) : null}

      {secao === 'pendentes' ? (
        <Text style={styles.cardAcao}>Toque para revisar e confirmar</Text>
      ) : null}

      {/* RN-18 — o cancelamento foi pedido e uma das ordens ainda não confirmou
          no SAP. Não é acionável (o vendedor já mandou cancelar) e não é
          histórico (ela ainda pode voltar viva do retry). */}
      {secao === 'emCancelamento' ? (
        <Text style={styles.cardAviso}>
          Cancelamento em andamento. Aguarde a confirmação.
        </Text>
      ) : null}
    </TouchableOpacity>
  );

  const Secao = ({
    titulo,
    descricao,
    itens,
    secao,
  }: {
    titulo: string;
    descricao?: string;
    itens: ScheduledOrderMenuItem[];
    secao: 'emConsulta' | 'pendentes' | 'emCancelamento' | 'historico';
  }) => {
    if (!itens.length) {
      return null;
    }
    return (
      <View style={styles.secao}>
        <Text style={styles.secaoTitulo}>{titulo}</Text>
        {descricao ? (
          <Text style={styles.secaoDescricao}>{descricao}</Text>
        ) : null}
        {itens.map(item => (
          <Card key={item.setId} item={item} secao={secao} />
        ))}
      </View>
    );
  };

  const vazio =
    !menu.emConsulta.length &&
    !menu.pendentes.length &&
    !menu.historico.length;

  return (
    <View style={styles.container}>
      <Header justDropDown={false} />

      <ScrollView
        contentContainerStyle={styles.conteudo}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              carregar(false);
            }}
          />
        }>
        {/* O `Header` global não tem seta: ele é o topo da Home. Esta é uma
            tela de segundo nível, alcançada pelo menu lateral e pela
            notificação, e sem a seta o cliente fica sem saída — o mesmo padrão
            de Débitos e Favoritos resolve. */}
        <SearchHeader onPress={goBack} title="Pedidos programados" mb={4} />
        <Text style={styles.subtituloTela}>
          Pedidos montados pelo seu vendedor para uma data futura.
        </Text>

        {loading ? (
          <ActivityIndicator
            style={styles.spinner}
            color={Colors.RedPallete.red}
          />
        ) : erro ? (
          <View style={styles.estadoVazio}>
            <Text style={styles.estadoVazioTitulo}>
              Não conseguimos carregar seus pedidos programados
            </Text>
            <TouchableOpacity onPress={() => carregar(true)}>
              <Text style={styles.tentarNovamente}>Tentar novamente</Text>
            </TouchableOpacity>
          </View>
        ) : vazio ? (
          <View style={styles.estadoVazio}>
            <Text style={styles.estadoVazioTitulo}>
              Você não tem pedidos programados
            </Text>
            <Text style={styles.estadoVazioTexto}>
              Quando seu vendedor montar um pedido para uma data futura, ele
              aparece aqui.
            </Text>
          </View>
        ) : (
          <>
            <Secao
              titulo="Aguardando sua confirmação"
              itens={menu.pendentes}
              secao="pendentes"
            />
            <Secao
              titulo="Cancelamento em andamento"
              descricao="Seu vendedor cancelou estes pedidos e estamos concluindo."
              itens={menu.emCancelamento}
              secao="emCancelamento"
            />
            <Secao
              titulo="Em consulta"
              descricao="Você pode conferir agora; a data de confirmação está indicada em cada pedido."
              itens={menu.emConsulta}
              secao="emConsulta"
            />
            <Secao
              titulo="Histórico"
              itens={menu.historico}
              secao="historico"
            />
          </>
        )}
      </ScrollView>
    </View>
  );
};

export default ScheduledOrderMenu;
